import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../shared/services/apiClient';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { isAdmin } from '../../../utils/permissions';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import ResponsiveChart from '../../../shared/ui/ResponsiveChart';
import { Building2, Users, LayoutDashboard, Database, RefreshCw, Activity, TrendingUp } from 'lucide-react';
import { queryKeys } from '../../../shared/services/queryKeys';

type Company = { id: number; name: string };
type DashboardStat = { companyId: number; companyName: string; count: number };
type Activity = { id: number; text: string; date: string };
type AdminUser = { name?: string; companyId?: number; externalCompanyName?: string };
type AdminDashboard = { companyId?: number; empresaId?: string | number };
type AdminDatasource = { companyId?: number };

// Custom Tooltip con glassmorphism
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className="bg-slate-900/95 backdrop-blur-xl border border-sky-400/30 rounded-xl p-4 shadow-2xl"
      style={{ 
        position: 'relative',
        zIndex: 99999,
        pointerEvents: 'none',
        minWidth: '200px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6), 0 0 30px rgba(14, 165, 233, 0.15)'
      }}
    >
      {label && (
        <p className="text-white text-sm font-bold mb-3 pb-2 border-b border-sky-400/20">
          {label}
        </p>
      )}
      {payload.map((entry: any, index: number) => (
        <div key={index} className="flex items-center justify-between gap-4 mt-2">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full flex-shrink-0 shadow-lg" 
              style={{ 
                backgroundColor: entry.fill || '#0EA5E9',
                boxShadow: `0 0 10px ${entry.fill || '#0EA5E9'}40`
              }}
            />
            <span className="text-slate-200 text-xs font-medium">
              Dashboards
            </span>
          </div>
          <span className="text-sky-400 text-base font-bold tabular-nums">
            {entry.value}
          </span>
        </div>
      ))}
    </motion.div>
  );
};

// KPI Card Component con animaciones y navegación
const KPICard: React.FC<{ 
  icon: React.ReactNode; 
  label: string; 
  value: number; 
  delay: number;
  color: string;
  onClick?: () => void;
}> = ({ icon, label, value, delay, color, onClick }) => (
  <motion.button
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    whileHover={{ y: -4, transition: { duration: 0.2 } }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="relative group text-left w-full cursor-pointer"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-sky-500/10 to-transparent rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    <div className="relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-sky-500/30 transition-all duration-300">
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-lg bg-gradient-to-br ${color}`}>
          {icon}
        </div>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: delay + 0.3, type: 'spring' }}
          className="text-sky-400/20"
        >
          <TrendingUp size={20} />
        </motion.div>
      </div>
      <div className="space-y-1">
        <p className="text-slate-400 text-sm font-medium">{label}</p>
        <motion.p 
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={{ delay: delay + 0.2, type: 'spring' }}
          className="text-white text-3xl font-bold tabular-nums"
        >
          {value.toLocaleString()}
        </motion.p>
      </div>
    </div>
  </motion.button>
);

const AdminOverview: React.FC<{ 
  currentUser?: any; 
  selectedCompanyId?: string | number; 
  companiesProp?: Company[];
  setCurrentView?: (view: string) => void;
}> = ({ currentUser, selectedCompanyId, companiesProp, setCurrentView }) => {
  // resolve currentUser if not passed as prop - use useMemo to recalculate when currentUser changes
  const resolvedUser = useMemo(() => {
    if (currentUser) return currentUser;
    try { 
      return JSON.parse(localStorage.getItem('currentUser') || 'null'); 
    } catch { 
      return null; 
    }
  }, [currentUser]);

  // Debug: log user and permission check
  useEffect(() => {
    console.log('=== AdminOverview Debug ===');
    console.log('currentUser prop:', currentUser);
    console.log('resolvedUser:', resolvedUser);
    console.log('resolvedUser.role:', resolvedUser?.role);
    console.log('resolvedUser.userType:', resolvedUser?.userType);
    console.log('isAdmin(resolvedUser):', isAdmin(resolvedUser));
  }, [currentUser, resolvedUser]);

  const companiesQuery = useQuery<Company[]>({
    queryKey: queryKeys.companies,
    queryFn: async () => {
      const res = await api.get('/companies');
      return Array.isArray(res.data) ? res.data : (res.data?.companies || []);
    },
    enabled: Boolean(resolvedUser && isAdmin(resolvedUser)),
    initialData: companiesProp && companiesProp.length ? companiesProp : undefined,
    staleTime: 120_000,
  });

  const usersQuery = useQuery<AdminUser[]>({
    queryKey: queryKeys.users,
    queryFn: async () => {
      const res = await api.get('/users');
      return Array.isArray(res.data) ? res.data : (res.data?.users || []);
    },
    enabled: Boolean(resolvedUser && isAdmin(resolvedUser)),
    staleTime: 120_000,
  });

  const dashboardsQuery = useQuery<AdminDashboard[]>({
    queryKey: queryKeys.dashboards,
    queryFn: async () => {
      const res = await api.get('/dashboards');
      return Array.isArray(res.data) ? res.data : (res.data?.dashboards || []);
    },
    enabled: Boolean(resolvedUser && isAdmin(resolvedUser)),
    staleTime: 120_000,
  });

  const datasourcesQuery = useQuery<AdminDatasource[]>({
    queryKey: queryKeys.datasources,
    queryFn: async () => {
      const res = await api.get('/datasources');
      return Array.isArray(res.data) ? res.data : (res.data?.datasources || []);
    },
    enabled: Boolean(resolvedUser && isAdmin(resolvedUser)),
    staleTime: 120_000,
  });

  const companies = companiesQuery.data ?? [];
  const users = usersQuery.data ?? [];
  const dashboards = dashboardsQuery.data ?? [];
  const datasources = datasourcesQuery.data ?? [];

  const loading = companiesQuery.isLoading || usersQuery.isLoading || dashboardsQuery.isLoading || datasourcesQuery.isLoading;
  const refreshing = companiesQuery.isFetching || usersQuery.isFetching || dashboardsQuery.isFetching || datasourcesQuery.isFetching;

  const companyFilter = typeof selectedCompanyId === 'number' ? Number(selectedCompanyId) : undefined;

  const usersCount = useMemo(() => {
    const filtered = companyFilter
      ? users.filter((u) => u.companyId === companyFilter || ((u.externalCompanyName || '').trim().toLowerCase() === (companies.find(c => c.id === companyFilter)?.name || '').trim().toLowerCase()))
      : users;
    return filtered.length;
  }, [companies, companyFilter, users]);

  const dashboardsCount = useMemo(() => {
    const filtered = companyFilter ? dashboards.filter(d => d.companyId === companyFilter) : dashboards;
    return filtered.length;
  }, [companyFilter, dashboards]);

  const datasourcesCount = useMemo(() => {
    const filtered = companyFilter ? datasources.filter(ds => ds.companyId === companyFilter) : datasources;
    return filtered.length;
  }, [companyFilter, datasources]);

  const rawChartData = useMemo(() => {
    const statsMap = new Map<string, { name: string; count: number }>();
    dashboards.forEach((d: any) => {
      const companyId = d.empresaId || d.companyId || 'unknown';
      if (!statsMap.has(companyId)) {
        statsMap.set(companyId, { name: 'Empresa ' + companyId, count: 0 });
      }
      statsMap.get(companyId)!.count++;
    });
    return Array.from(statsMap.entries()).map(([id, data]) => ({
      companyId: typeof id === 'string' ? parseInt(id, 10) || 0 : id,
      companyName: companies.find((c: any) => c.id === id)?.name || data.name,
      count: data.count,
    }));
  }, [companies, dashboards]);

  const chartData = useMemo(() => {
    if (!companyFilter) return rawChartData;
    return rawChartData.filter((cd) => cd.companyId === companyFilter);
  }, [companyFilter, rawChartData]);

  const rawRecent = useMemo<Activity[]>(() => [
    { id: 1, text: 'Sistema inicializado correctamente', date: new Date().toLocaleDateString() },
  ], []);

  const recent = useMemo(() => {
    if (!companyFilter) return rawRecent.slice(0, 5);
    return rawRecent.filter((act) => {
      const actorName = (act.text || '').split(' ')[0] || '';
      const usr = users.find(u => (u.name || '').trim() === actorName.trim());
      if (!usr) return false;
      return usr.companyId === companyFilter || ((usr.externalCompanyName || '').trim().toLowerCase() === (companies.find(c => c.id === companyFilter)?.name || '').trim().toLowerCase());
    }).slice(0, 5);
  }, [companies, companyFilter, rawRecent, users]);

  if (!resolvedUser || !isAdmin(resolvedUser)) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-8 bg-slate-900 min-h-[400px] rounded-xl"
      >
        <div className="flex flex-col items-center justify-center h-full space-y-4">
          <div className="p-4 bg-red-500/10 rounded-full">
            <svg className="w-16 h-16 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white">Acceso denegado</h2>
          <p className="text-slate-400">No tienes permisos para ver este panel.</p>
        </div>
      </motion.div>
    );
  }  return (
    <div className="p-8 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 min-h-screen">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-8"
      >
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent mb-2">
            Panel de Administración
          </h1>
          <p className="text-slate-400 flex items-center gap-2">
            <Activity size={16} />
            Resumen global del sistema
          </p>
        </div>
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={async () => {
            try {
              await Promise.all([
                companiesQuery.refetch(),
                usersQuery.refetch(),
                dashboardsQuery.refetch(),
                datasourcesQuery.refetch(),
              ]);
              toast.success('Datos actualizados correctamente');
            } catch (err) {
              console.error('Error loading admin overview', err);
              toast.error('Error al cargar datos');
            }
          }}
          disabled={refreshing}
          className="flex items-center gap-2 bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white px-6 py-3 rounded-lg font-medium shadow-lg shadow-sky-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          Actualizar
        </motion.button>
      </motion.div>

      {loading && !companies.length ? (
        <div className="space-y-6">
          {/* KPI Skeletons */}
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6">
            {[1,2,3,4].map(i => (
              <motion.div 
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 animate-pulse"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-slate-700 rounded-lg" />
                  <div className="w-5 h-5 bg-slate-700 rounded" />
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-slate-700 rounded w-2/3" />
                  <div className="h-8 bg-slate-700 rounded w-1/2" />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Chart and Activity Skeletons */}
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 h-80 animate-pulse" />
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <div className="h-6 bg-slate-700 rounded w-1/2 mb-4" />
              <div className="space-y-3">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="flex justify-between">
                    <div className="h-4 bg-slate-700 rounded w-2/3" />
                    <div className="h-4 bg-slate-700 rounded w-1/4" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* KPIs */}
            <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6 mb-8">
              <KPICard 
                icon={<Building2 size={24} className="text-blue-400" />}
                label="Total de empresas"
                value={companies.length}
                delay={0}
                color="from-blue-500/20 to-blue-600/10"
                onClick={() => setCurrentView && setCurrentView('companies')}
              />
              <KPICard 
                icon={<Users size={24} className="text-sky-400" />}
                label="Total de usuarios"
                value={usersCount}
                delay={0.1}
                color="from-sky-500/20 to-sky-600/10"
                onClick={() => setCurrentView && setCurrentView('users-internal')}
              />
              <KPICard 
                icon={<LayoutDashboard size={24} className="text-purple-400" />}
                label="Total de dashboards"
                value={dashboardsCount}
                delay={0.2}
                color="from-purple-500/20 to-purple-600/10"
                onClick={() => setCurrentView && setCurrentView('dashboards')}
              />
              <KPICard 
                icon={<Database size={24} className="text-orange-400" />}
                label="Total de fuentes de datos"
                value={datasourcesCount}
                delay={0.3}
                color="from-orange-500/20 to-orange-600/10"
                onClick={() => setCurrentView && setCurrentView('datasources')}
              />
            </div>

            {/* Chart + Activity */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Chart */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="lg:col-span-2 relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-sky-500/10 to-transparent rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-sky-500/30 transition-all duration-300">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-sky-500/10 rounded-lg">
                      <LayoutDashboard size={20} className="text-sky-400" />
                    </div>
                    <h3 className="text-sky-400 font-semibold text-lg">Dashboards por Empresa</h3>
                  </div>
                  
                  {chartData.length === 0 ? (
                    <div className="h-80 flex flex-col items-center justify-center">
                      <div className="relative">
                        <motion.div
                          animate={{ scale: [1, 1.05, 1], opacity: [0.5, 0.8, 0.5] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                          className="absolute inset-0 bg-sky-500/10 rounded-full blur-2xl"
                        />
                        <div className="relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 text-center">
                          <svg className="w-16 h-16 mx-auto mb-3 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <p className="text-slate-400 text-sm font-medium">Sin datos disponibles</p>
                          <p className="text-slate-600 text-xs mt-1">No hay dashboards para mostrar</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-80">
                      <ResponsiveChart minHeight={320}>
                        {({ width, height }) => (
                          <BarChart width={width} height={height} data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 50 }}>
                            <defs>
                              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#0EA5E9" stopOpacity={1} />
                                <stop offset="100%" stopColor="#0284C7" stopOpacity={0.9} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" opacity={0.3} />
                            <XAxis 
                              dataKey="companyName" 
                              stroke="#94a3b8" 
                              tick={{ fill: '#94a3b8', fontSize: 11 }}
                              tickLine={false}
                              angle={-25}
                              textAnchor="end"
                              height={70}
                              interval={0}
                            />
                            <YAxis 
                              stroke="#94a3b8" 
                              tick={{ fill: '#94a3b8', fontSize: 11 }}
                              tickLine={false}
                              axisLine={false}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(14, 165, 233, 0.1)' }} />
                            <Bar 
                              dataKey="count" 
                              fill="url(#barGradient)" 
                              radius={[8, 8, 0, 0]}
                              animationDuration={1000}
                              cursor="pointer"
                            />
                          </BarChart>
                        )}
                      </ResponsiveChart>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Activity */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-purple-500/30 transition-all duration-300 h-full">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-purple-500/10 rounded-lg">
                      <Activity size={20} className="text-purple-400" />
                    </div>
                    <h3 className="text-purple-400 font-semibold text-lg">Actividad reciente</h3>
                  </div>
                  
                  <div className="space-y-3">
                    {recent.length === 0 ? (
                      <div className="py-8 text-center">
                        <div className="inline-flex p-4 bg-slate-700/30 rounded-full mb-3">
                          <Activity size={24} className="text-slate-500" />
                        </div>
                        <p className="text-slate-400 text-sm">No hay actividad reciente</p>
                      </div>
                    ) : (
                      recent.map((r, idx) => (
                        <motion.div
                          key={r.id}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.6 + idx * 0.1 }}
                          className="group/item flex items-start gap-3 p-3 rounded-lg hover:bg-slate-700/30 transition-colors cursor-pointer"
                        >
                          <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-sky-400 shadow-lg shadow-sky-400/50" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium group-hover/item:text-sky-400 transition-colors truncate">
                              {r.text}
                            </p>
                            <p className="text-slate-500 text-xs mt-1 flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {r.date}
                            </p>
                          </div>
                        </motion.div>
                      ))
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

export default AdminOverview;
